/**
 * SuccessPage - Order confirmation after successful payment
 * Route: /order/:sessionId/success
 */

import { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useBrandTheme } from "@/lib/brand-theme";
import { BrandLogo } from "@/components/BrandLogo";
import { formatCurrency, formatDate, cn, getIsFreeItemForOrderItem } from "@/lib/utils";
import { analyticsApi } from "@/api/client";
import { Card } from "@/components/Card";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useOrder, useSession } from "@/api/hooks";
import { useOrderDetailsStore } from "@/lib/order-details-store";
import { useTheme } from "@/lib/theme/ThemeProvider";

// Secure checkout badge with theme toggle
function SecureBadge() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className="flex items-center gap-3">
      <div className="secure-badge">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span>Order Complete</span>
      </div>
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-[var(--color-surface-secondary)] transition-colors"
        aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
        title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      >
        {resolvedTheme === "dark" ? (
          <svg
            className="w-5 h-5 text-(--color-text-secondary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-(--color-text-secondary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

export function SuccessPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const paymentReference = searchParams.get("reference");
  const { resolvedTheme } = useTheme();

  const [selectedShippingMethod, setSelectedShippingMethod] = useState<{
    id: string;
    name: string;
    description: string;
    price_cents: number;
  } | null>(null);
  const { theme } = useBrandTheme();

  // Use persisted order details from store (same data as OrderPage/PaymentPage)
  const orderDetailsStore = useOrderDetailsStore();
  const storeCartItems = useMemo(
    () => orderDetailsStore.cartItemsByBrand.flatMap(([, items]) => items),
    [orderDetailsStore.cartItemsByBrand],
  );

  const hasTrackedGTMPurchase = useRef(false);
  const hasTrackedPostHogOrderCompleted = useRef(false);

  const { data: orderData, isLoading, error } = useOrder(orderId);
  const { data: sessionData } = useSession(sessionId || null);

  // Calculate totals from store data (same as OrderPage/PaymentPage)
  const calculatedSubtotalCents = storeCartItems.reduce((sum, item) => sum + item.line_total_cents, 0);
  const calculatedShippingCents = orderDetailsStore.shippingCents;
  const calculatedDiscountCents = orderDetailsStore.totalAppliedDiscount + orderDetailsStore.automatedDiscountCents;
  const subtotalCents = orderDetailsStore.paymentSubtotalCents ?? calculatedSubtotalCents;
  const shippingCents = orderDetailsStore.paymentShippingCents ?? calculatedShippingCents;
  const discountTotal = orderDetailsStore.paymentDiscountCents ?? calculatedDiscountCents;
  // VAT (15%) applies to the items subtotal only - shipping and discounts are excluded
  const taxCents = orderDetailsStore.paymentTaxCents ?? Math.round(subtotalCents * 0.15);
  const activeDiscountCode =
    [...new Set(orderDetailsStore.totalDiscountDetail.map((d) => d.code))].join(",") || null;
  const hasDiscount = discountTotal > 0 || Boolean(activeDiscountCode);
  const totalPaidCents =
    orderDetailsStore.paymentTotalCents ??
    subtotalCents + shippingCents - discountTotal + taxCents;

  // Load shipping method from localStorage
  useEffect(() => {
    const savedShippingMethod = localStorage.getItem("selectedShippingMethod");
    if (savedShippingMethod) {
      try {
        const shippingOption = JSON.parse(savedShippingMethod);
        setSelectedShippingMethod(shippingOption);
      } catch (error) {
        console.error("Failed to parse shipping method from localStorage:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "SuccessPage", action: "parseShippingMethod" },
          });
        }
      }
    }
  }, []);

  // Track payment_made event via the external analytics API (only once per order)
  useEffect(() => {
    if (
      orderData &&
      sessionId &&
      sessionData?.session?.retailer_id &&
      !hasTrackedPostHogOrderCompleted.current
    ) {
      hasTrackedPostHogOrderCompleted.current = true;
      void analyticsApi.track({
        session_id: sessionId,
        event: "payment_made",
        properties: {
          retailer_id: sessionData.session.retailer_id,
          session_id: sessionId,
          order_id: orderData.order.id,
          total: totalPaidCents / 100,
          creator_id: orderData.order.customer_id || null,
        },
      });
    }
  }, [orderData, sessionId, sessionData, totalPaidCents]);

  // Track GTM purchase event when order loads (only once per order)
  useEffect(() => {
    if (orderData && sessionData && !hasTrackedGTMPurchase.current) {
      hasTrackedGTMPurchase.current = true;
      try {
        window.dataLayer?.push({
          event: "purchase",
          transaction_id: orderData.order.id,
          value: totalPaidCents / 100,
          currency: "ZAR",
          link_id: sessionData.session?.context?.attribution?.linkId,
          utm_source: sessionData.session?.context?.attribution?.utmSource,
          utm_medium: sessionData.session?.context?.attribution?.utmMedium,
          utm_campaign: sessionData.session?.context?.attribution?.utmCampaign,
        });
      } catch (error) {
        console.error("GTM purchase error:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "SuccessPage", action: "gtmPurchase" },
          });
        }
      }
    }
  }, [orderData, sessionData, totalPaidCents]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-(--color-bg-primary) flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-(--color-bg-primary) flex items-center justify-center p-4">
        <ErrorMessage
          title="Failed to load order"
          message={error instanceof Error ? error.message : "Order not found"}
          className="max-w-md"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-bg-primary) flex flex-col justify-between gap-5">
      {/* Header */}
      <div>
        <header className="bg-(--color-surface-primary) border-b border-(--color-surface-border-light)">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <BrandLogo brand={theme} />
              <SecureBadge />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-8">
            {/* Main Content */}
            <div>
              {/* Success Message */}
              <div className="text-center mb-8">
                <div className="mx-auto w-16 h-16 bg-[var(--color-success-light)] rounded-full flex items-center justify-center mb-4">
                  <svg
                    className="w-8 h-8 text-[var(--color-success)]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>

                <h1 className="text-[24px] font-semibold text-(--color-text-primary) mb-2">
                  Thank you for your order!
                </h1>
                <p className="text-[14px] text-(--color-text-secondary)">
                  We've received your payment and your order is being processed.
                </p>
              </div>

              {/* Order Details */}
              {orderData && (
                <>
                  <Card className="mb-6">
                    <div className="flex items-center justify-between mb-5">
                      <h2 className="text-[16px] font-semibold text-(--color-text-primary)">
                        Order Details
                      </h2>
                      <span className="px-2.5 py-1 text-[12px] font-medium bg-[var(--color-success-light)] text-[var(--color-success)] rounded-full">
                        {orderData.order.status === "paid" ? "Paid" : orderData.order.status}
                      </span>
                    </div>

                    <div className="space-y-3 text-[14px]">
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">Order Number</span>
                        <span className="font-medium text-(--color-text-primary) font-mono text-[13px]">
                          {orderData.order.id.slice(0, 8).toUpperCase()}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">Order Date</span>
                        <span className="font-medium text-(--color-text-primary)">
                          {formatDate(orderData.order.created_at)}
                        </span>
                      </div>

                      {(paymentReference || orderData.order.payment_reference) && (
                        <div className="flex justify-between">
                          <span className="text-(--color-text-secondary)">
                            Payment Reference
                          </span>
                          <span className="font-medium text-(--color-text-primary) font-mono text-[13px]">
                            {paymentReference || orderData.order.payment_reference}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">Payment Status</span>
                        <span className="font-medium text-[var(--color-success)]">Confirmed</span>
                      </div>
                    </div>
                  </Card>

                  {/* Shipping Information */}
                  <Card className="mb-6">
                    <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
                      Shipping Address
                    </h2>

                    <div className="text-[14px] text-[#545454] space-y-3">
                      <p className="text-(--color-text-secondary)">
                        {orderData?.order?.shipping_info?.first_name} {orderData?.order?.shipping_info?.last_name}
                      </p>
                      <p className="text-(--color-text-secondary)">
                        {orderData?.order?.shipping_info?.address}
                        {orderData?.order?.shipping_info?.address2 &&
                        `, ${orderData?.order?.shipping_info?.address2}`}
                      </p>
                      <p className="text-(--color-text-secondary)">
                        {orderData?.order?.shipping_info?.city},{" "}
                        {orderData?.order?.shipping_info?.suburb},{" "}
                        {orderData?.order?.shipping_info?.province},{" "}
                        {orderData?.order?.shipping_info?.postal_code}
                      </p>
                      <p className="text-(--color-text-secondary)">
                        {orderData?.order?.shipping_info?.phone}
                      </p>
                      {orderData?.order?.shipping_info?.email && (
                        <p className="text-(--color-text-secondary)">
                          {orderData?.order?.shipping_info?.email}
                        </p>
                      )}
                    </div>
                  </Card>
                </>
              )}
            </div>

            {/* Order Summary Sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-8">
                {orderData && (
                  <Card>
                    <h2 className="text-[16px] font-semibold text-(--color-text-primary) pb-4 border-b border-(--color-surface-border-light)">
                      Order Summary
                    </h2>

                    {/* Items */}
                    <div className="border-b border-(--color-surface-border-light) pb-4 mb-4">
                    {storeCartItems.map((item) => {
                      // Determine which image to display: variant image if selected, otherwise product image
                      const displayImage = item.variant_image && item.variant_image.trim() !== ""
                        ? item.variant_image
                        : item.image_url;
                      const isFreeItem = getIsFreeItemForOrderItem({
                        product_id: item.product_id,
                        sku: item.sku,
                        quantity: item.quantity,
                        line_total_cents: item.line_total_cents,
                        unit_price_cents: item.unit_price_cents,
                      });

                      return (
                        <div key={item.id} className="flex items-center gap-3 py-2">
                          <div className="relative">
                            <div className="product-thumbnail">
                              {displayImage ? (
                                <img
                                  src={displayImage}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <svg
                                  className="w-6 h-6 text-(--color-text-placeholder)"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-text-muted)] text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                              {item.quantity}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-medium text-(--color-text-primary) truncate">
                              {item.title}
                            </p>
                            {item.selected_variant && (
                              <p className="text-[12px] text-(--color-text-muted) mt-0.5">
                                Variant: {item.selected_variant}
                              </p>
                            )}
                            <p className="truncate text-[12px] text-(--color-text-muted) mt-0.5">
                              Quantity: {item.quantity}
                            </p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span
                              className={cn(
                                "text-[13px] font-medium tabular-nums",
                                isFreeItem
                                  ? "text-[var(--color-success)] line-through"
                                  : "text-(--color-text-primary)",
                              )}
                            >
                              {formatCurrency(item.line_total_cents)}
                            </span>
                            {isFreeItem === true && (
                              <p className="text-[12px] text-(--color-text-primary) font-medium mt-0.5">
                                Free
                              </p>
                            )}
                            {typeof isFreeItem === "number" && (
                              <p className="text-[12px] text-(--color-text-primary) font-medium tabular-nums mt-0.5">
                                {formatCurrency(isFreeItem)}
                              </p>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                    {/* Totals */}
                    <div className="border-t border-(--color-surface-border-light) pt-4 space-y-3">
                      <div className="flex justify-between text-[14px]">
                        <span className="text-(--color-text-secondary)">Subtotal</span>
                        <span className="text-(--color-text-primary) tabular-nums">
                          {formatCurrency(subtotalCents)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">Shipping</span>
                        <span className="text-(--color-text-primary) font-medium tabular-nums">
                          {shippingCents > 0 ? formatCurrency(shippingCents) : "—"}
                        </span>
                      </div>
                      {selectedShippingMethod && shippingCents > 0 && (
                        <p className="text-[12px] text-(--color-text-muted) -mt-1">
                          {selectedShippingMethod.name}
                        </p>
                      )}
                      {hasDiscount && (
                        <div className="flex justify-between items-baseline">
                          <span className="text-(--color-text-secondary)">Total discounts</span>
                          <span className="text-[var(--color-success)] font-medium tabular-nums">
                            -{formatCurrency(discountTotal)}
                          </span>
                        </div>
                      )}
                      {taxCents > 0 && (
                        <div className="flex justify-between">
                          <span className="text-(--color-text-secondary)">Tax</span>
                          <span className="text-(--color-text-primary) font-medium tabular-nums">
                            {formatCurrency(taxCents)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between pt-3 border-t border-(--color-surface-border-light)">
                        <span className="text-[16px] font-semibold text-(--color-text-primary)">
                          Total Paid
                        </span>
                        <span className="text-[18px] font-semibold text-(--color-text-primary) tabular-nums">
                          {formatCurrency(totalPaidCents)}
                        </span>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Support */}
                <div className="mt-6 text-center text-[13px] text-(--color-text-muted)">
                  <p>
                    Questions about your order?{" "}
                    <a
                      href="https://www.onnile.shop/contact"
                      target="_black"
                      rel="noopener noreferrer"
                      className="text-[var(--color-secondary,var(--color-primary))] hover:text-[var(--color-secondary-hover,var(--color-primary-hover))] font-medium"
                    >
                      Contact Support
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Order Summary */}
          {orderData && (
            <div className="lg:hidden mt-8">
              <Card>
                <h2 className="text-[16px] font-semibold text-(--color-text-primary) pb-4 border-b border-(--color-surface-border-light)">
                  Order Summary
                </h2>

                <div className="divide-y divide-[var(--color-surface-border-light)]">
                  {storeCartItems.map((item) => {
                    const isFreeItem = getIsFreeItemForOrderItem({
                      product_id: item.product_id,
                      sku: item.sku,
                      quantity: item.quantity,
                      line_total_cents: item.line_total_cents,
                      unit_price_cents: item.unit_price_cents,
                    });
                    return (
                      <div key={item.id} className="py-3 flex justify-between">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div className="w-12 h-12 bg-[var(--color-bg-secondary)] rounded border border-(--color-surface-border-light) flex items-center justify-center overflow-hidden">
                              {item.image_url ? (
                                <img
                                  src={item.image_url}
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <svg
                                  className="w-6 h-6 text-(--color-text-placeholder)"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                  />
                                </svg>
                              )}
                            </div>
                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-text-muted)] text-white text-[10px] font-medium rounded-full flex items-center justify-center">
                              {item.quantity}
                            </span>
                          </div>

                          <div>
                            <p className="text-[14px] font-medium text-(--color-text-primary)">
                              {item.title}
                            </p>
                            <p className="text-[12px] text-(--color-text-muted)">
                              SKU: {item.sku} × {item.quantity}
                            </p>
                          </div>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <span
                            className={cn(
                              "text-[14px] font-medium tabular-nums",
                              isFreeItem
                                ? "text-[var(--color-success)] line-through"
                                : "text-(--color-text-primary)",
                            )}
                          >
                            {formatCurrency(item.line_total_cents)}
                          </span>
                          {isFreeItem === true && (
                            <p className="text-[12px] text-(--color-text-primary) font-medium mt-0.5">
                              Free
                            </p>
                          )}
                          {typeof isFreeItem === "number" && (
                            <p className="text-[12px] text-(--color-text-primary) font-medium tabular-nums mt-0.5">
                              {formatCurrency(isFreeItem)}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="border-t border-(--color-surface-border-light) pt-4 mt-4 space-y-2">
                  <div className="flex justify-between text-[14px]">
                    <span className="text-(--color-text-secondary)">Subtotal</span>
                    <span className="text-(--color-text-primary) tabular-nums">
                      {formatCurrency(subtotalCents)}
                    </span>
                  </div>
                  {selectedShippingMethod && shippingCents > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-(--color-text-secondary)">Shipping</span>
                        <span className="tabular-nums">{formatCurrency(shippingCents)}</span>
                      </div>
                      <p className="text-[12px] text-(--color-text-muted) -mt-1">
                        {selectedShippingMethod.name}
                      </p>
                    </>
                  )}
                  {hasDiscount && (
                    <div className="flex justify-between items-baseline">
                      <span className="text-(--color-text-secondary)">Total discounts</span>
                      <span className="text-[var(--color-success)] font-medium tabular-nums">
                        -{formatCurrency(discountTotal)}
                      </span>
                    </div>
                  )}
                  {taxCents > 0 && (
                    <div className="flex justify-between">
                      <span className="text-(--color-text-secondary)">Tax</span>
                      <span className="tabular-nums">{formatCurrency(taxCents)}</span>
                    </div>
                  )}

                  <div className="flex justify-between pt-2 border-t border-(--color-surface-border-light)">
                    <span className="font-semibold text-(--color-text-primary)">
                      Total Paid
                    </span>
                    <span className="font-semibold text-(--color-text-primary) tabular-nums">
                      {formatCurrency(totalPaidCents)}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Mobile Support */}
              <div className="mt-6 text-center text-[13px] text-(--color-text-muted)">
                <p>
                  Questions about your order?{" "}
                  <a
                    href="https://www.onnile.shop/contact"
                    target="_black"
                    rel="noopener noreferrer"
                    className="text-[var(--color-secondary,var(--color-primary))] hover:text-[var(--color-secondary-hover,var(--color-primary-hover))] font-medium"
                  >
                    Contact Support
                  </a>
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      <footer className="flex items-center border-t border-(--color-surface-border-light) p-4 z-50 items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5 mb-1" />
        ) : (
          <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5 mb-1" />
        )}
      </footer>
    </div>
  );
}
